/**
 * Node visual para incorporação de conteúdo externo (site, vídeo, PDF)
 * Exibe ícone, label e preview da URL configurada
 * 
 * RELEASE: 1.0.0 - 2026-03-08 - Novo node de incorporação
 */

import { Handle, Position, NodeProps } from '@xyflow/react';
import { Code2 } from 'lucide-react';
import { FlowNodeData } from '@/types/flows';
import { FlowNodeToolbar } from './FlowNodeToolbar';

/**
 * Componente visual do node de incorporação (embed/iframe)
 */
export function EmbedNode({ data, id, selected }: NodeProps) {
  const nodeData = data as FlowNodeData;

  // Callbacks injetados via FlowCanvas
  const onEdit = (nodeData as any).onEdit;
  const onDuplicate = (nodeData as any).onDuplicate;
  const onDelete = (nodeData as any).onDelete;
  const onTestFromHere = (nodeData as any).onTestFromHere;

  // Extrai configuração do node
  const url = (nodeData.config?.url as string) || '';
  const height = (nodeData.config?.height as number) || 400;

  // Gera preview da URL (trunca se muito longa)
  const urlPreview = url
    ? url.length > 40 ? `🌐 ${url.substring(0, 37)}...` : `🌐 ${url}`
    : 'URL não configurada';

  return (
    <div className="bg-card rounded-lg shadow-lg border-2 border-sky-500 min-w-[200px] max-w-[240px]">
      {/* Toolbar flutuante */}
      <FlowNodeToolbar
        nodeId={id}
        nodeType={nodeData.type}
        onEdit={onEdit}
        onDuplicate={onDuplicate}
        onDelete={onDelete}
        onTestFromHere={onTestFromHere}
        isVisible={selected}
      />

      <Handle
        type="target"
        position={Position.Top}
        className="!bg-sky-500 !w-3 !h-3"
      />

      <div className="p-3">
        {/* Cabeçalho com ícone e nome */}
        <div className="flex items-center gap-2 mb-2">
          <div className="bg-sky-500/10 text-sky-600 p-2 rounded">
            <Code2 className="h-4 w-4" />
          </div>
          <div className="flex-1">
            <div className="font-semibold text-sm text-foreground">
              {(nodeData.config?.nickname as string) || 'Incorporar'}
            </div>
          </div>
        </div>

        {/* Preview da URL e altura */}
        <div className="text-xs text-muted-foreground bg-muted/50 rounded p-2 space-y-1">
          <div className="truncate">{urlPreview}</div>
          {url && (
            <div className="text-muted-foreground/70">Altura: {height}px</div>
          )}
        </div>
      </div>

      <Handle
        type="source"
        position={Position.Bottom}
        className="!bg-sky-500 !w-3 !h-3"
      />
    </div>
  );
}
